import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LoginService } from '../login.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ErrorHandlerService } from '../../../common/services/error-handler.service';
import { convertToEnglishDigits } from '../../../common/utils/string';
import { PermissionService } from '../../../common/services/permission.service';
import { TokenService } from '../../../common/services/token.service';
import { SwalService } from '../../../services/swal/swal.service';

@Component({
  selector: 'ngx-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  loginMode: 'password' | 'otp' = 'password';
  otpStep: 'request' | 'verify' = 'request';
  loading = false;

  resendLoading = false;
  resendCooldown = 120;
  resendRemainingSeconds = 0;
  private resendInterval: any;

  constructor(
    private loginService: LoginService,
    private router: Router,
    private fb: FormBuilder,
    private errorHandler: ErrorHandlerService,
    private permissionService: PermissionService,
    private tokenService: TokenService,
    private readonly swalService: SwalService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      phone: ['', [Validators.required]],
      password: ['', [Validators.required]],
      code: [''],
    });

    if (this.tokenService.getToken()) {
      this.router.navigate(['/pages/dashboard']);
      return;
    }

    this.updateValidators();
  }

  ngOnDestroy(): void {
    this.clearResendTimer();
  }

  onSubmit() {
    this.updateValidators();

    if (this.form.invalid || this.loading) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.loginMode === 'password') {
      this.submitPasswordLogin();
      return;
    }

    if (this.otpStep === 'request') {
      this.submitRequestOtp();
      return;
    }

    this.submitOtpLogin();
  }

  setLoginMode(mode: 'password' | 'otp') {
    if (this.loading || this.loginMode === mode) {
      return;
    }

    this.loginMode = mode;
    this.otpStep = 'request';
    this.form.get('password')?.reset();
    this.form.get('code')?.reset();
    this.resendLoading = false;
    this.resendRemainingSeconds = 0;
    this.clearResendTimer();
    this.updateValidators();
  }

  handleChangePhone() {
    this.otpStep = 'request';
    this.form.get('code')?.reset();
    this.clearResendTimer();
    this.resendRemainingSeconds = 0;
    this.resendLoading = false;
    this.updateValidators();
  }

  resendCode() {
    if (!this.canResendCode) {
      return;
    }

    const phone = convertToEnglishDigits(this.form.value.phone);
    if (!phone) return;

    this.resendLoading = true;

    this.loginService.requestOtp(phone).subscribe({
      next: (res) => {
        this.resendLoading = false;
        this.swalService.success(
          res?.data?.message || 'کد تایید مجدداً ارسال شد.',
          undefined,
          { illustration: 'lock' },
        );
        this.startResendTimer();
      },
      error: (err) => {
        this.resendLoading = false;
        this.errorHandler.handle(err, this.form);
      },
    });
  }

  get canResendCode(): boolean {
    return (
      this.loginMode === 'otp' &&
      this.otpStep === 'verify' &&
      !this.loading &&
      !this.resendLoading &&
      this.resendRemainingSeconds === 0
    );
  }

  get submitButtonLabel(): string {
    if (this.loading) {
      return this.loginMode === 'otp' && this.otpStep === 'request'
        ? 'درحال ارسال...'
        : 'درحال ورود...';
    }

    if (this.loginMode === 'password') {
      return 'ورود';
    }

    return this.otpStep === 'request' ? 'دریافت کد تایید' : 'ورود با کد تایید';
  }

  private submitPasswordLogin() {
    const phone = convertToEnglishDigits(this.form.value.phone);
    const password = convertToEnglishDigits(this.form.value.password);
    this.loading = true;

    this.loginService.loginWithPassword(phone, password).subscribe({
      next: async () => {
        await this.finishLogin();
      },
      error: (err) => {
        console.log('login error', err);
        this.loading = false;
        this.errorHandler.handle(err, this.form);
      },
    });
  }

  private submitRequestOtp() {
    const phone = convertToEnglishDigits(this.form.value.phone);
    this.loading = true;

    this.loginService.requestOtp(phone).subscribe({
      next: (res) => {
        this.loading = false;
        this.otpStep = 'verify';
        this.updateValidators();
        this.swalService.success(
          res?.data?.message || 'کد تایید ارسال شد.',
          undefined,
          { illustration: 'lock' },
        );
        this.startResendTimer();
      },
      error: (err) => {
        console.error('OTP error', err);
        this.loading = false;
        this.errorHandler.handle(err, this.form);
      },
    });
  }

  private submitOtpLogin() {
    const phone = convertToEnglishDigits(this.form.value.phone);
    const code = convertToEnglishDigits(this.form.value.code);
    this.loading = true;

    this.loginService.login(phone, code).subscribe({
      next: async () => {
        await this.finishLogin();
      },
      error: (err) => {
        console.log('login error', err);
        this.loading = false;
        this.errorHandler.handle(err, this.form);
      },
    });
  }

  private async finishLogin() {
    try {
      await this.permissionService.loadPermissionsFromProfile();
    } catch (err) {
      console.warn('Failed to load permissions after login', err);
    } finally {
      this.loading = false;
      this.router.navigate(['/pages/dashboard']);
    }
  }

  private updateValidators() {
    this.form?.get('phone')?.setValidators([Validators.required]);
    this.form
      ?.get('password')
      ?.setValidators(
        this.loginMode === 'password' ? [Validators.required] : []
      );
    this.form
      ?.get('code')
      ?.setValidators(
        this.loginMode === 'otp' && this.otpStep === 'verify'
          ? [Validators.required]
          : []
      );

    this.form?.get('phone')?.updateValueAndValidity({ emitEvent: false });
    this.form?.get('password')?.updateValueAndValidity({ emitEvent: false });
    this.form?.get('code')?.updateValueAndValidity({ emitEvent: false });
  }

  private startResendTimer() {
    this.clearResendTimer();
    this.resendRemainingSeconds = this.resendCooldown;

    this.resendInterval = setInterval(() => {
      if (this.resendRemainingSeconds > 0) {
        this.resendRemainingSeconds--;
      } else {
        this.clearResendTimer();
      }
    }, 1000);
  }

  private clearResendTimer() {
    if (this.resendInterval) {
      clearInterval(this.resendInterval);
      this.resendInterval = null;
    }
  }
}
